import { GitCommitIcon } from "../icons";
import { formatRelativeTime } from "../format";
import type { CommitRow } from "../api/contracts/commits";

interface CommitListProps {
  commits: CommitRow[];
  emptyHint?: string;
  /** Optional per-row link_kind labels (e.g. when commits come from a task↔commit join). */
  linkKindByCommitId?: Record<number, "auto_in_progress" | "message_ref" | "manual">;
}

const LINK_KIND_LABEL: Record<string, string> = {
  auto_in_progress: "claim",
  message_ref: "task ref",
  manual: "manual",
};

function shortSha(sha: string): string {
  return sha.slice(0, 7);
}

/**
 * Renders a list of commits. Used by:
 * - TaskCommitsSection (task peek body extension)
 * - /team/<actor> profile pages
 * - /settings/repos detail panes
 *
 * No surrounding Card — caller wraps. Empty state is a single-line muted hint.
 */
export function CommitList({ commits, emptyHint, linkKindByCommitId }: CommitListProps) {
  if (commits.length === 0) {
    return (
      <div className="type-body text-muted-foreground py-2">
        {emptyHint ?? "No commits linked yet."}
      </div>
    );
  }
  return (
    <ul className="divide-y divide-border/40">
      {commits.map((c) => {
        const linkKind = linkKindByCommitId?.[c.id];
        return (
          <li key={c.id} className="flex items-start gap-3 py-2 first:pt-0 last:pb-0">
            <GitCommitIcon className="size-4 mt-0.5 text-muted-foreground shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="type-body text-foreground truncate">
                {c.subject ?? "(no subject)"}
              </div>
              <div className="type-subtext text-muted-foreground flex items-center gap-2 mt-0.5">
                <code className="font-mono">{shortSha(c.sha)}</code>
                <span>·</span>
                <span>{c.author_name ?? c.author_email ?? "unknown"}</span>
                <span>·</span>
                <span>{formatRelativeTime(c.committed_at)}</span>
                {linkKind ? (
                  <>
                    <span>·</span>
                    <span className="text-muted-foreground/80">
                      {LINK_KIND_LABEL[linkKind] ?? linkKind}
                    </span>
                  </>
                ) : null}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
